/**
 * Advisor Universe — the pure pieces shared by the loader (scripts/advisor-universe) and the
 * server, so a role or a marketing-fit score means the same thing wherever it is computed.
 *
 * Inputs are only ever what the sources actually say. A field we don't know earns nothing:
 * "unknown" is never treated as a signal.
 */

import type { AdvisorRole } from '@poseidon/types/types/advisorUniverse.type';

// Order matters: the first rule that matches wins, so "Founding Partner" is a founder and
// "Chief Compliance Officer" is operations, not a founder.
const ROLE_RULES: ReadonlyArray<readonly [AdvisorRole, RegExp]> = [
  [
    'operations',
    /\b(chief (operating|compliance|marketing) officer|coo|cco|cmo|compliance|operations|marketing|client service|administrat|assistant|paraplanner)\b/i,
  ],
  ['founder', /\b(founder|founding|co-?founder|owner|ceo|chief executive|president)\b/i],
  ['partner', /\b(partner|managing director|md)\b/i],
  [
    'principal',
    /\b(principal|chief investment officer|cio|executive director|regional director|branch manager|director)\b/i,
  ],
  ['wealth_manager', /\b(wealth (manager|advisor|adviser|management)|private wealth|private client)\b/i],
  [
    'advisor',
    /\b(financial (advisor|adviser|planner|consultant|representative|professional|services (professional|representative))|investment (advisor|adviser|representative|consultant|counselor)|relationship manager|portfolio manager|(private|premier) banker|advisor|adviser|planner|cfp|registered representative|agent)\b/i,
  ],
  // Support staff last, so "Associate Financial Advisor" stays an advisor.
  [
    'operations',
    /\b(associate|analyst|intern|wholesaler|project manager|account (manager|executive)|administrator|chief financial officer|cfo)\b/i,
  ],
];

/**
 * Classify a job title into a coarse role. Pass the best title first (contacts_db's LinkedIn-derived
 * title, then AdvisorGuide's sparse title). No usable title → 'advisor' (everyone in the universe is a
 * registered advisor); a title that matches nothing → 'other'.
 */
export function classifyAdvisorRole(...titles: ReadonlyArray<string | null | undefined>): AdvisorRole {
  const title = titles.map((t) => cleanText(t)).find((t) => t != null);
  if (title == null) return 'advisor';
  // "Vice President" is a wirehouse rank, not a presidency: drop it so "Senior Vice President,
  // Wealth Management" classifies on what's left, and a bare "Vice President" is an advisor.
  const withoutVp = title.replace(/\b((senior|executive|first|assistant) )?vice[- ]president\b|\b[se]?vp\b/gi, ' ');
  const hadVp = withoutVp !== title;
  for (const [role, re] of ROLE_RULES) if (re.test(withoutVp)) return role;
  return hadVp ? 'advisor' : 'other';
}

/** LinkedIn slug: the part after /in/, without query/fragment/trailing slash, decoded and lowercased. */
export function linkedinSlugFromUrl(url: string | null | undefined): string | null {
  const u = cleanText(url);
  if (u == null) return null;
  const m = /\/in\/([^/?#]+)/i.exec(u);
  if (!m?.[1]) return null;
  try {
    return decodeURIComponent(m[1]).toLowerCase();
  } catch {
    return m[1].toLowerCase();
  }
}

/** Trim, and treat empty strings and the bulk-dump null marker `\N` as missing. */
export function cleanText(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const t = raw.trim();
  return t === '' || t === '\\N' || t.toLowerCase() === 'null' ? null : t;
}

/**
 * Normalize a US/Canada (NANP) phone number to E.164 (`+1XXXXXXXXXX`), or null when it isn't a
 * plausible NANP number. Handles punctuation, a leading 1/+1, and the "doubled" numbers bulk dumps
 * contain (the same 10 digits written twice).
 */
export function normalizeUsPhone(raw: string | null | undefined): string | null {
  const text = cleanText(raw);
  if (text == null) return null;
  let digits = text.replace(/\D/g, '');
  if (digits.length === 20 && digits.slice(0, 10) === digits.slice(10)) digits = digits.slice(0, 10);
  if (digits.length === 22 && digits.slice(0, 11) === digits.slice(11)) digits = digits.slice(0, 11);
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return null;
  // NANP: area code and exchange can't start with 0 or 1; 555-01xx is fictional.
  if (/^[01]/.test(digits) || /^[01]/.test(digits.slice(3))) return null;
  if (digits.slice(3, 6) === '555' && digits.slice(6, 8) === '01') return null;
  return `+1${digits}`;
}

export interface MarketingFitInputs {
  /** Firm regulatory AUM in USD; null when not reported. */
  aum: number | null;
  /** Active advisors at the firm (AdvisorGuide); null when unknown. */
  activeAdvisorCount: number | null;
  /** Whether the firm's ADV lists a website; null when unknown. */
  hasWebsite: boolean | null;
  /** Firm formed (earliest current registration) within the last 3 years; null when unknown. */
  isNewFirm: boolean | null;
  /** HNW individual clients ÷ all individual clients, 0–1; null when not reported. */
  hnwShare: number | null;
  /** Median LinkedIn connections among the firm's advisors we can see; null when none are visible. */
  medianLinkedinConnections: number | null;
  /** Share of the firm's active advisors with a LinkedIn profile, 0–1; null when the count is unknown. */
  linkedinShare: number | null;
  /** Conference signals for the firm (v1.1); absent = none on file. See {@link applyEventSignals}. */
  events?: FirmEventSignals;
}

/** What the 2026 conference lists say about anyone at a firm (scripts/advisor-universe/events.mts). */
export interface FirmEventSignals {
  /** Someone at the firm flagged marketing / digital / lead-gen interest at FutureProof. */
  marketingInterest: boolean;
  /** Someone at the firm is on a 2026 RIA conference list (attended, met us, or asked for a meeting). */
  attendedConference: boolean;
}

export interface MarketingFit {
  /** 0–100. */
  score: number;
  /** Human-readable reasons, in the order the points were earned. */
  reasons: string[];
}

/** Above this many active advisors, a missing website is treated as missing data, not a signal. */
export const NO_WEBSITE_MAX_ADVISORS = 50;

export const MARKETING_FIT_REASONS = {
  growthStage: 'Growth-stage RIA ($50M–$1B)',
  established: 'Established RIA ($1B–$3B)',
  smallTeam: 'Small team, likely no marketing staff',
  noWebsite: 'No website on file',
  newFirm: 'Firm formed in last 3 yrs',
  hnwFocused: 'HNW-focused',
  lightLinkedin: 'Light LinkedIn presence',
  marketingInterest: 'Expressed marketing interest at FutureProof',
  attendedConference: 'Attended a 2026 RIA conference',
} as const;

/**
 * Fold advisor-level conference tags (`universe.event_tag`) up to each advisor's firm. Any `interest_*`
 * tag counts as marketing interest; any tag at all means someone at the firm is on a 2026 list.
 */
export function firmEventSignals(
  rows: Iterable<{ firmCrd: string; tag: string }>,
): Map<string, FirmEventSignals> {
  const out = new Map<string, FirmEventSignals>();
  for (const r of rows) {
    const s = out.get(r.firmCrd) ?? { marketingInterest: false, attendedConference: false };
    s.attendedConference = true;
    if (r.tag.startsWith('interest_')) s.marketingInterest = true;
    out.set(r.firmCrd, s);
  }
  return out;
}

/**
 * Add the conference points to a firm's base fit — the ONE place they're defined. The loader stores
 * the base score separately so re-running the events step (or a universe rebuild) re-derives the
 * total from the base instead of stacking points twice.
 */
export function applyEventSignals(base: MarketingFit, signals: FirmEventSignals | undefined): MarketingFit {
  if (signals == null) return { score: Math.min(100, base.score), reasons: [...base.reasons] };
  let score = base.score;
  const reasons = [...base.reasons];
  if (signals.marketingInterest) {
    score += 15;
    reasons.push(MARKETING_FIT_REASONS.marketingInterest);
  }
  if (signals.attendedConference) {
    score += 5;
    reasons.push(MARKETING_FIT_REASONS.attendedConference);
  }
  return { score: Math.min(100, score), reasons };
}

/**
 * How likely a firm is to need (and buy) outbound marketing help — a transparent points score, not a
 * model. Every point comes with its reason so the UI can show why a firm ranks where it does.
 */
export function scoreMarketingFit(f: MarketingFitInputs): MarketingFit {
  let score = 0;
  const reasons: string[] = [];
  const add = (points: number, reason: string) => {
    score += points;
    reasons.push(reason);
  };

  if (f.aum != null && f.aum >= 50_000_000 && f.aum < 1_000_000_000) add(25, MARKETING_FIT_REASONS.growthStage);
  else if (f.aum != null && f.aum >= 1_000_000_000 && f.aum < 3_000_000_000) add(10, MARKETING_FIT_REASONS.established);
  if (f.activeAdvisorCount != null && f.activeAdvisorCount >= 1 && f.activeAdvisorCount <= 10)
    add(20, MARKETING_FIT_REASONS.smallTeam);
  // AdvisorGuide is missing websites for some large firms (Edward Jones), so "no website" only counts
  // for firms small enough that a missing website is plausibly real.
  if (f.hasWebsite === false && f.activeAdvisorCount != null && f.activeAdvisorCount <= NO_WEBSITE_MAX_ADVISORS)
    add(15, MARKETING_FIT_REASONS.noWebsite);
  if (f.isNewFirm === true) add(20, MARKETING_FIT_REASONS.newFirm);
  if (f.hnwShare != null && f.hnwShare >= 0.5) add(10, MARKETING_FIT_REASONS.hnwFocused);
  const lightLinkedin =
    (f.medianLinkedinConnections != null && f.medianLinkedinConnections < 500) ||
    (f.linkedinShare != null && f.linkedinShare < 0.5);
  if (lightLinkedin) add(10, MARKETING_FIT_REASONS.lightLinkedin);

  // Uncapped here so the event points add on top of the real base before the single cap.
  return applyEventSignals({ score, reasons }, f.events);
}

/** Median of a list of numbers; null for an empty list. */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? (sorted[mid] ?? null) : ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

/** contact_sourced_field names that hold an email a person shared on LinkedIn. */
export const LINKEDIN_EMAIL_FIELDS = ['professionalEmail', 'personalEmail', 'linkedinEmail'] as const;
export const LINKEDIN_PHONE_FIELDS = ['phone'] as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Lowercased, trimmed email, or null when it isn't a plausible address. */
export function cleanLinkedinEmail(raw: string | null | undefined): string | null {
  const text =
    cleanText(raw)
      ?.toLowerCase()
      .replace(/^mailto:/, '') ?? null;
  return text != null && EMAIL_RE.test(text) ? text : null;
}

export interface LinkedinContactSourceRow {
  /** Lowercased LinkedIn slug. */
  slug: string;
  fieldName: string;
  data: string | null;
}

/**
 * Fold sourced-field rows (Poseidon already stored them from LinkedIn profiles) into one entry per
 * slug: deduped, cleaned emails and E.164 phones. Rows with nothing usable are dropped, so a slug
 * only gets an entry when there is something to show.
 */
export function buildLinkedinContactEntries(
  rows: readonly LinkedinContactSourceRow[],
): Map<string, { emails: string[]; phones: string[] }> {
  const out = new Map<string, { emails: Set<string>; phones: Set<string> }>();
  for (const r of rows) {
    const slug = cleanText(r.slug)?.toLowerCase();
    if (slug == null) continue;
    const email = (LINKEDIN_EMAIL_FIELDS as readonly string[]).includes(r.fieldName)
      ? cleanLinkedinEmail(r.data)
      : null;
    const phone = (LINKEDIN_PHONE_FIELDS as readonly string[]).includes(r.fieldName) ? normalizeUsPhone(r.data) : null;
    if (email == null && phone == null) continue;
    const entry = out.get(slug) ?? { emails: new Set<string>(), phones: new Set<string>() };
    if (email != null) entry.emails.add(email);
    if (phone != null) entry.phones.add(phone);
    out.set(slug, entry);
  }
  return new Map([...out].map(([slug, e]) => [slug, { emails: [...e.emails].sort(), phones: [...e.phones].sort() }]));
}

/** Contact info from a fetched LinkedIn profile's `contact_info`, cleaned the same way. */
export function cleanLinkedinContactInfo(
  info: { emails?: readonly (string | null)[] | null; phones?: readonly (string | null)[] | null } | null | undefined,
): {
  emails: string[];
  phones: string[];
} {
  const emails = new Set<string>();
  const phones = new Set<string>();
  for (const e of info?.emails ?? []) {
    const c = cleanLinkedinEmail(e);
    if (c != null) emails.add(c);
  }
  for (const p of info?.phones ?? []) {
    const c = normalizeUsPhone(p);
    if (c != null) phones.add(c);
  }
  return { emails: [...emails].sort(), phones: [...phones].sort() };
}

// ---------------------------------------------------------------------------------------------
// ADV Part 1A Item 5 — households and fee model, from the SEC's monthly Firm Roster FOIA file
// ---------------------------------------------------------------------------------------------

/** Item 5.D/5.E fields for one firm, as parsed from a Firm Roster row. */
export interface AdvRosterFirm {
  firmCrd: string;
  /** 5.D(a)(1) individual clients + 5.D(b)(1) high-net-worth individual clients. */
  households: number | null;
  /** Their RAUM, 5.D(a)(3) + 5.D(b)(3), ÷ households, rounded USD. */
  aumPerHousehold: number | null;
  fees: {
    aum: boolean | null;
    hourly: boolean | null;
    subscription: boolean | null;
    fixed: boolean | null;
    commissions: boolean | null;
    performance: boolean | null;
  };
  /** Latest ADV filing date, ISO (yyyy-mm-dd). */
  filedOn: string | null;
}

/** "  1,234.00 " → 1234; blanks and junk → null. */
export function parseAdvNumber(raw: string | null | undefined): number | null {
  const t = (raw ?? '').replace(/[,\s$]/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function advFlag(raw: string | null | undefined): boolean | null {
  const t = (raw ?? '').trim().toUpperCase();
  return t === 'Y' ? true : t === 'N' ? false : null;
}

/** "06/26/2025" → "2025-06-26". */
function advDate(raw: string | null | undefined): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec((raw ?? '').trim());
  if (m == null) return null;
  const [, mo, d, y] = m;
  return `${y}-${(mo ?? '').padStart(2, '0')}-${(d ?? '').padStart(2, '0')}`;
}

/**
 * One Firm Roster row → households + fee model. Households are the two INDIVIDUAL client types (the
 * proxy RIAs use for households); institutional clients are left out on purpose. A firm that reports
 * neither count has unknown households (null), never 0 — and AUM per household is only computed from
 * the RAUM of those same client types, never from firm-wide AUM.
 */
export function parseAdvRosterRow(row: Readonly<Record<string, string | undefined>>): AdvRosterFirm | null {
  const firmCrd = (row['Organization CRD#'] ?? '').trim();
  if (!/^\d+$/.test(firmCrd)) return null;
  const ind = parseAdvNumber(row['5D(a)(1)']);
  const hnw = parseAdvNumber(row['5D(b)(1)']);
  const households = ind == null && hnw == null ? null : Math.round((ind ?? 0) + (hnw ?? 0));
  const indAum = parseAdvNumber(row['5D(a)(3)']);
  const hnwAum = parseAdvNumber(row['5D(b)(3)']);
  const raum = indAum == null && hnwAum == null ? null : (indAum ?? 0) + (hnwAum ?? 0);
  return {
    firmCrd,
    households,
    aumPerHousehold: households != null && households > 0 && raum != null ? Math.round(raum / households) : null,
    fees: {
      aum: advFlag(row['5E(1)']),
      hourly: advFlag(row['5E(2)']),
      subscription: advFlag(row['5E(3)']),
      fixed: advFlag(row['5E(4)']),
      commissions: advFlag(row['5E(5)']),
      performance: advFlag(row['5E(6)']),
    },
    filedOn: advDate(row['Latest ADV Filing Date']),
  };
}

// ---------------------------------------------------------------------------------------------
// Phone kinds — cell vs office vs firm line
// ---------------------------------------------------------------------------------------------

/**
 * `contact_sourced_field.source` for each kind of number RIA Search copies onto a contact. The kind
 * lives in the source so every surface can label it (a cell is dialed first; a firm line is the
 * switchboard). Bulk "mobile" data is often an office line: on 2026-10-14, 48% of universe
 * "mobiles" were shared by 2+ advisors and 7% were the firm's SEC main line, and 59% of the cells
 * RIA Search had copied onto contacts were later marked wrong or firm numbers.
 */
export const RIA_PHONE_SOURCE = {
  cell: 'advisor-universe-cell',
  office: 'advisor-universe-office',
  firm: 'advisor-universe-firm',
} as const;

export type RiaPhoneKind = keyof typeof RIA_PHONE_SOURCE;

/** The kind of a stored phone, read from its `contact_sourced_field.source`; null when unknown. */
export function phoneKindFromSource(source: string | null | undefined): RiaPhoneKind | null {
  if (source === RIA_PHONE_SOURCE.cell) return 'cell';
  if (source === RIA_PHONE_SOURCE.office) return 'office';
  if (source === RIA_PHONE_SOURCE.firm) return 'firm';
  return null;
}

/** What every surface calls each kind — one wording so the sidebar, dialer and Call button agree
    ("Firm number" is the product's existing name for the firm's line: badge, mark action, outcome). */
export const PHONE_KIND_LABEL: Record<RiaPhoneKind, string> = {
  cell: 'Cell',
  office: 'Office line',
  firm: 'Firm number',
};

/** The last 10 digits of a US number, or null — the comparison key for "same number". */
export function phoneDigits10(raw: string | null | undefined): string | null {
  const e164 = normalizeUsPhone(raw);
  return e164 == null ? null : e164.replace(/\D/g, '').slice(-10);
}

/**
 * Which numbers to give a contact and in what order (the first becomes primary when the contact has
 * no phone yet): a real cell first, then an office line, then the firm's SEC main line.
 * - A number equal to the firm line is the firm line, wherever it came from.
 * - A number shared by 2+ advisors (`sharedDigits`) is an office/switchboard line, not a cell.
 * - A LinkedIn-shared number beats the bulk mobile (the person published it themselves).
 */
export function planRiaPhones(input: {
  linkedinPhones: readonly string[];
  bulkMobile: string | null;
  firmPhone: string | null;
  sharedDigits: ReadonlySet<string>;
}): { kind: RiaPhoneKind; phone: string; source: string }[] {
  const firmPhone = normalizeUsPhone(input.firmPhone);
  const firm10 = phoneDigits10(firmPhone);
  let cell: string | null = null;
  let office: string | null = null;
  for (const raw of [...input.linkedinPhones, input.bulkMobile]) {
    const phone = normalizeUsPhone(raw);
    const d10 = phoneDigits10(phone);
    if (phone == null || d10 == null || d10 === firm10) continue;
    if (input.sharedDigits.has(d10)) office ??= phone;
    else cell ??= phone;
  }
  const out: { kind: RiaPhoneKind; phone: string; source: string }[] = [];
  if (cell != null) out.push({ kind: 'cell', phone: cell, source: RIA_PHONE_SOURCE.cell });
  if (office != null && phoneDigits10(office) !== phoneDigits10(cell)) {
    out.push({ kind: 'office', phone: office, source: RIA_PHONE_SOURCE.office });
  }
  if (firmPhone != null) out.push({ kind: 'firm', phone: firmPhone, source: RIA_PHONE_SOURCE.firm });
  return out;
}
